import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save,
  X,
  Plus,
  Calendar,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Clock,
  BarChart3,
  CheckCircle,
  Upload,
  Link,
  Tag,
  MapPin,
  Phone,
  Mail,
  Globe,
  Building,
  User,
  Briefcase,
  PieChart,
  TrendingUp,
  Shield,
  Zap,
  Database
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageHeader from '../common/PageHeader';
import PageBreadcrumb from '../common/PageBreadcrumb';
import FormStepper from '../common/FormStepper';
import API_CONFIG from '../../config/api';
import MasterDataService from '../../services/masterDataService';
import AutoIncrementService from '../../services/autoIncrementService';

const CreateProject = () => {
  const navigate = useNavigate();
  
  // Tab management
  const [currentStep, setCurrentStep] = useState(0);
  
  // Test mode state
  const [testMode, setTestMode] = useState(false);
  
  // Project categories from master data
  const [projectCategories, setProjectCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [projectManagers, setProjectManagers] = useState([]);

  // Form state - Initialize with empty/default values
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    description: '',
    category: '',
    priority: 'Medium',
    status: 'Planning',
    projectCode: '',
    department: '',
    projectType: '',
    businessUnit: '',
    
    // Timeline & Budget
    startDate: '',
    endDate: '',
    estimatedDuration: '',
    budget: '',
    currency: 'USD',
    costCenter: '',
    
    projectManager: '',

    // Finance & Budgeting
    budgetBreakdown: {
      labor: 0,
      materials: 0,
      equipment: 0,
      travel: 0,
      other: 0
    },
    expenseCategories: [],
    financialMilestones: [],
    budgetApprovalRequired: false,
    budgetApprover: '',
    costAllocation: '',
    roiTarget: '',
    financialReporting: 'monthly',
    budgetVarianceThreshold: 0,
    expenseApprovalLimit: 0,
    
    // Billing Milestones
    billingMilestones: [],
    
    // Team & Resources
    stakeholders: [],
    externalVendors: [],
    resources: [],
    stakeholderDetails: [],
    
    // Project Details
    objectives: [],
    deliverables: [],
    milestones: [],
    risks: [],
    assumptions: [],
    constraints: [],
    
    // Communication & Reporting
    reportingFrequency: 'weekly',
    communicationChannels: [],
    meetingSchedule: '',
    reportingTemplate: '',
    
    // Integration & Tools
    projectTools: [],
    integrations: [],
    documentRepository: '',
    knowledgeBase: '',
    
    // Compliance & Security
    complianceRequirements: [],
    securityLevel: 'medium',
    dataClassification: 'internal',
    approvalWorkflow: 'standard',
    
    // Advanced Settings
    autoNotifications: true,
    timeTracking: true,
    budgetAlerts: true,
    riskMonitoring: true,
    customFields: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const { success, error: showError, toasts, removeToast } = useToast();

  const STEPS = [
    { id: 'basic', label: 'Project Setup', icon: FileText, hint: 'Basic project information' },
    { id: 'team', label: 'Team & Planning', icon: Users, hint: 'Team, stakeholders, and resources' },
    { id: 'finance', label: 'Finance & Budgeting', icon: DollarSign, hint: 'Budget, milestones, and financial controls' },
    { id: 'communication', label: 'Communication & Tools', icon: Globe, hint: 'Reporting and integrations' },
    { id: 'advanced', label: 'Advanced Settings', icon: Settings, hint: 'Notifications, compliance, and approvals' },
  ];

  const handleNext = (e) => {
    if (e) e.preventDefault();
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (index) => {
    if (index <= currentStep) {
      setCurrentStep(index);
    }
  };

  // Load test mode setting and project categories on component mount
  // Preview project code on component mount (without incrementing)
  // Preview project code on component mount
  useEffect(() => {
    const previewProjectCode = async () => {
      
      const localCode = `PRJ-${Math.floor(100000 + Math.random() * 900000)}`;
      setFormData(prev => ({ ...prev, projectCode: localCode }));
      
    };
    
    previewProjectCode();
  }, []);

  useEffect(() => {
    const loadTestMode = async () => {
      try {
        const response = await fetch(API_CONFIG.ENDPOINTS.SETTINGS, {
          headers: API_CONFIG.HEADERS
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setTestMode(result.data.testMode ?? false);
          }
        }
      } catch (error) {
        console.error('Error loading test mode setting:', error);
      }
    };
    
    const loadProjectCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await MasterDataService.getByCategory('PROJECTCATEGORY');
        if (response.success && response.data) {
          // Filter only active categories and map to dropdown format
          const categories = response.data
            .filter(item => item.isActive)
            .map(item => ({
              code: item.code,
              label: item.label,
              description: item.description
            }));
          setProjectCategories(categories);
        }
      } catch (error) {
        console.error('Error loading project categories:', error);
        // Fallback to empty array if API fails
        setProjectCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    loadTestMode();
    loadProjectCategories();
  }, []);
  
  useEffect(() => {
    const fetchProjectManagers = async () => {
      try {
        const response = await fetch(API_CONFIG.ENDPOINTS.USERS, {
          headers: API_CONFIG.HEADERS
        });

        if (!response.ok) throw new Error('Failed to load team members');

        const result = await response.json();
        const members = result.data || [];

        // console.log("Raw Team Data:", members);

        const managers = members
          .filter(member => {
            // 1. Check if user is active
            if (member.isActive === false) return false;

            // 2. FIX: Check 'position' instead of 'role'
            // We use optional chaining (?.) to avoid crashing if position is missing
            const position = member.position || "";
            return position.toLowerCase().includes('manager');
          })
          .map(member => ({
            _id: member._id || member.id,
            // 3. FIX: Combine firstName and lastName
            name: `${member.firstName} ${member.lastName}`,
            // 4. FIX: Use position for the label
            role: member.position
          }));

        // console.log("Filtered Managers:", managers); 
        setProjectManagers(managers);
      } catch (err) {
        console.error('Failed to load project managers', err);
        setProjectManagers([]);
      }
    };

    fetchProjectManagers();
  }, []);
  
   
  // Function to fill form with test data
  const fillTestData = () => {
    const testData = {
      // Basic Information
      name: 'Luxury Residential Development - Sunset Hills',
      description: 'Development of a premium 200-unit luxury residential complex featuring smart home technology, sustainable design, and world-class amenities including a clubhouse, fitness center, and landscaped gardens.',
      category: 'infrastructure',
      priority: 'Critical',
      status: 'Planning',
      // projectCode is excluded - it's auto-generated and should not be overwritten
      department: 'development',
      projectType: 'client',
      businessUnit: 'West Coast',
      
      // Timeline & Budget
      startDate: '2024-03-01',
      endDate: '2026-08-31',
      estimatedDuration: '30 months',
      budget: '45000000',
      currency: 'USD',
      costCenter: 'CC-DEV-001',
      
      // Finance & Budgeting
      budgetBreakdown: {
        labor: 12000000,
        materials: 18000000,
        equipment: 8000000,
        travel: 500000,
        other: 6500000
      },
      expenseCategories: ['Construction Materials', 'Permits & Licenses', 'Architectural Services', 'Legal & Compliance', 'Marketing & Sales'],
      financialMilestones: [
        { name: 'Q1 2024 Review', amount: 11250000, date: '2024-03-31' },
        { name: 'Q2 2024 Review', amount: 11250000, date: '2024-06-30' },
        { name: 'Q3 2024 Review', amount: 11250000, date: '2024-09-30' },
        { name: 'Q4 2024 Review', amount: 11250000, date: '2024-12-31' }
      ],
      budgetApprovalRequired: true,
      budgetApprover: 'ceo',
      costAllocation: 'Development Department',
      roiTarget: '35',
      financialReporting: 'monthly',
      budgetVarianceThreshold: 10,
      expenseApprovalLimit: 25000,
      
      // Billing Milestones
      billingMilestones: [
        {
          name: 'Site Preparation & Permits',
          billingDate: '2024-04-30',
          amount: 8000000,
          currency: 'USD',
          trigger: 'milestone',
          description: 'Site clearing, permits, and initial infrastructure setup',
          autoGenerate: true
        },
        {
          name: 'Foundation & Structure',
          billingDate: '2024-09-30',
          amount: 15000000,
          currency: 'USD',
          trigger: 'milestone',
          description: 'Foundation work and structural framework completion',
          autoGenerate: true
        },
        {
          name: 'Building Envelope',
          billingDate: '2025-03-31',
          amount: 12000000,
          currency: 'USD',
          trigger: 'milestone',
          description: 'Exterior walls, roofing, and weatherproofing',
          autoGenerate: true
        },
        {
          name: 'Interior & Finishing',
          billingDate: '2025-09-30',
          amount: 10000000,
          currency: 'USD',
          trigger: 'milestone',
          description: 'Interior construction, fixtures, and finishing work',
          autoGenerate: true
        }
      ],
      
      // Team & Resources
      stakeholders: ['City Planning Commission', 'Environmental Agency', 'Local Community Board', 'Investor Group'],
      externalVendors: ['ABC Construction', 'Premier Architects', 'GreenTech Engineering', 'Luxury Interiors Inc'],
      resources: [
        {
          name: 'Michael Rodriguez',
          type: 'employee',
          role: 'Senior Project Manager',
          rate: 180,
          currency: 'USD',
          hours: 50,
          startDate: '2024-03-01',
          endDate: '2026-08-31',
          status: 'active',
          skills: ['Construction Management', 'Permit Coordination', 'Budget Control'],
          experience: 'senior',
          location: 'Los Angeles',
          contact: 'michael.rodriguez@sunsetrealty.com',
          notes: '15+ years in luxury residential development'
        },
        {
          name: 'Jennifer Park',
          type: 'contractor',
          role: 'Lead Architect',
          rate: 200,
          currency: 'USD',
          hours: 40,
          startDate: '2024-02-15',
          endDate: '2025-12-31',
          status: 'active',
          skills: ['Sustainable Design', 'Luxury Residential', 'Smart Home Integration'],
          experience: 'expert',
          location: 'San Francisco',
          contact: 'jennifer.park@premierarchitects.com',
          notes: 'Award-winning architect specializing in luxury developments'
        }
      ],
      stakeholderDetails: [
        {
          name: 'Robert Chen',
          role: 'City Planning Commissioner',
          organization: 'City of Los Angeles',
          email: 'robert.chen@lacity.org',
          phone: '+1 (213) 555-0123',
          influence: 'critical',
          interest: 'high',
          availability: 'available',
          communicationPreference: 'meeting',
          involvement: 'frequent',
          notes: 'Key approval authority for permits and zoning'
        },
        {
          name: 'Sarah Thompson',
          role: 'Environmental Consultant',
          organization: 'GreenTech Environmental',
          email: 'sarah.thompson@greentech.com',
          phone: '+1 (310) 555-0456',
          influence: 'high',
          interest: 'high',
          availability: 'available',
          communicationPreference: 'email',
          involvement: 'regular',
          notes: 'Environmental impact assessment and sustainability compliance'
        }
      ],
      
      // Project Details
      objectives: [
        'Deliver 200 luxury residential units with premium finishes',
        'Achieve LEED Gold certification for sustainable design',
        'Implement smart home technology in all units',
        'Complete project within budget and timeline',
        'Achieve 95% pre-sales before construction completion'
      ],
      deliverables: [
        'Site preparation and infrastructure setup',
        'Foundation and structural framework',
        'Building envelope and weatherproofing',
        'Interior construction and luxury finishes',
        'Smart home systems and technology integration',
        'Landscaping and amenity areas',
        'Final inspections and occupancy permits'
      ],
      milestones: [
        {
          name: 'Site Preparation & Permits',
          description: 'Complete site clearing, permits, and initial infrastructure',
          dueDate: '2024-04-30',
          status: 'Not Started',
          deliverables: ['Site cleared', 'All permits approved', 'Utilities connected']
        },
        {
          name: 'Foundation & Structure',
          description: 'Complete foundation work and structural framework',
          dueDate: '2024-09-30',
          status: 'Not Started',
          deliverables: ['Foundation complete', 'Structural steel erected', 'Concrete work finished']
        },
        {
          name: 'Building Envelope',
          description: 'Complete exterior walls, roofing, and weatherproofing',
          dueDate: '2025-03-31',
          status: 'Not Started',
          deliverables: ['Exterior walls complete', 'Roofing installed', 'Windows and doors']
        },
        {
          name: 'Interior Construction',
          description: 'Complete interior framing, electrical, and plumbing',
          dueDate: '2025-09-30',
          status: 'Not Started',
          deliverables: ['Interior framing', 'Electrical rough-in', 'Plumbing installation']
        },
        {
          name: 'Finishing & Systems',
          description: 'Complete interior finishes and smart home systems',
          dueDate: '2026-03-31',
          status: 'Not Started',
          deliverables: ['Interior finishes', 'Smart home systems', 'HVAC installation']
        },
        {
          name: 'Landscaping & Amenities',
          description: 'Complete landscaping and amenity areas',
          dueDate: '2026-06-30',
          status: 'Not Started',
          deliverables: ['Landscaping complete', 'Clubhouse finished', 'Pool and fitness center']
        },
        {
          name: 'Final Inspections',
          description: 'Complete all inspections and obtain occupancy permits',
          dueDate: '2026-07-31',
          status: 'Not Started',
          deliverables: ['All inspections passed', 'Occupancy permits', 'Certificate of occupancy']
        },
        {
          name: 'Project Completion',
          description: 'Final walkthrough and project handover',
          dueDate: '2026-08-31',
          status: 'Not Started',
          deliverables: ['Final walkthrough', 'Warranty documentation', 'Project closeout']
        }
      ],
      risks: [
        {
          title: 'Permit delays and regulatory changes',
          description: 'Potential delays in obtaining permits or changes in building codes',
          impact: 'High',
          probability: 'Medium',
          status: 'Open',
          mitigationPlan: 'Engage with city officials early and maintain contingency timeline',
          assignedTo: null
        },
        {
          title: 'Material cost inflation',
          description: 'Rising costs of construction materials and labor',
          impact: 'High',
          probability: 'High',
          status: 'Open',
          mitigationPlan: 'Lock in material contracts early and maintain price escalation clauses',
          assignedTo: null
        },
        {
          title: 'Weather and environmental factors',
          description: 'Adverse weather conditions affecting construction timeline',
          impact: 'Medium',
          probability: 'High',
          status: 'Open',
          mitigationPlan: 'Build weather contingency into schedule and use weather-resistant materials',
          assignedTo: null
        },
        {
          title: 'Labor shortage and skilled worker availability',
          description: 'Shortage of skilled construction workers in the market',
          impact: 'High',
          probability: 'Medium',
          status: 'Open',
          mitigationPlan: 'Secure labor contracts early and provide competitive compensation packages',
          assignedTo: null
        },
        {
          title: 'Market conditions and sales velocity',
          description: 'Economic conditions affecting luxury real estate sales',
          impact: 'High',
          probability: 'Medium',
          status: 'Open',
          mitigationPlan: 'Implement flexible pricing strategy and maintain strong marketing presence',
          assignedTo: null
        }
      ],
      assumptions: [
        'Construction permits will be approved within 6 months',
        'Material costs will remain within 10% of current estimates',
        'Skilled labor will be available throughout the project duration',
        'Market conditions will remain favorable for luxury residential sales',
        'Environmental regulations will not change significantly during construction'
      ],
      constraints: [
        'Must comply with all local building codes and zoning requirements',
        'Budget cannot exceed $45M without board approval',
        'Must achieve LEED Gold certification for sustainability',
        'Construction must be completed by August 2026',
        'All units must include smart home technology integration'
      ],
      
      // Communication & Reporting
      reportingFrequency: 'weekly',
      communicationChannels: ['email', 'slack', 'teams'],
      meetingSchedule: 'Every Tuesday at 9:00 AM',
      reportingTemplate: 'Construction Progress Report',
      
      // Integration & Tools
      projectTools: ['procore', 'bim360', 'plangrid'],
      integrations: ['Slack', 'Microsoft Teams', 'AutoCAD', 'Revit'],
      documentRepository: 'Procore Document Management',
      knowledgeBase: 'Construction Wiki',
      
      // Compliance & Security
      complianceRequirements: ['OSHA', 'LEED', 'Local Building Codes', 'Environmental Regulations'],
      securityLevel: 'high',
      dataClassification: 'confidential',
      approvalWorkflow: 'executive',
      
      // Advanced Settings
      autoNotifications: true,
      timeTracking: true,
      budgetAlerts: true,
      riskMonitoring: true,
      customFields: ['Project Sponsor', 'LEED Certification Level', 'Zoning Category', 'Environmental Impact Score']
    };
    
    // Preserve the auto-generated project code when filling test data
    setFormData(prev => ({
      ...prev,
      ...testData,
      projectCode: prev.projectCode || '' 
    }));
    success('Test data filled successfully!');
  };

  // Debug: Log initial form data
  // console.log('Initial form data resources:', formData.resources);
  // console.log('Initial form data resources type:', typeof formData.resources);
  // console.log('Initial form data resources[0] type:', typeof formData.resources?.[0]);
  // console.log('Initial form data resources[0] value:', formData.resources?.[0]);
  // console.log('Initial form data resources is array:', Array.isArray(formData.resources));
  
  // Check if initial form data has stringified resources
  if (typeof formData.resources === 'string') {
    console.error('CRITICAL: Initial form data resources is a string!');
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayAdd = (field, value) => {
  setFormData(prev => ({
    ...prev,
    [field]: [...prev[field], value]
  }));
};


  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleBillingMilestoneChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      billingMilestones: prev.billingMilestones.map((milestone, i) => 
        i === index ? { ...milestone, [field]: value } : milestone
      )
    }));
  };

  const handleAddBillingMilestone = () => {
    const newMilestone = {
      name: '',
      billingDate: '',
      amount: '',
      currency: 'USD',
      trigger: 'date',
      description: '',
      autoGenerate: false
    };
    
    setFormData(prev => ({
      ...prev,
      billingMilestones: [...prev.billingMilestones, newMilestone]
    }));
  };

  const handleRemoveBillingMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      billingMilestones: prev.billingMilestones.filter((_, i) => i !== index)
    }));
  };

  const handleResourceChange = (index, field, value) => {
    setFormData(prev => {
      // Safety check: ensure resources is an array
      if (!Array.isArray(prev.resources)) {
        console.error('CRITICAL: Resources is not an array in handleResourceChange:', typeof prev.resources);
        return prev;
      }
      
      return {
        ...prev,
        resources: prev.resources.map((resource, i) => 
          i === index ? { ...resource, [field]: value } : resource
        )
      };
    });
  };

  const handleAddResource = () => {
    const newResource = {
      name: '',
      type: '',
      role: '',
      rate: '',
      currency: 'USD',
      hours: '',
      startDate: '',
      endDate: '',
      status: 'active',
      skills: [],
      experience: '',
      location: '',
      contact: '',
      notes: ''
    };
    
    setFormData(prev => {
      // Safety check: ensure resources is an array
      if (!Array.isArray(prev.resources)) {
        console.error('CRITICAL: Resources is not an array in handleAddResource:', typeof prev.resources);
        return {
          ...prev,
          resources: [newResource]
        };
      }
      
      return {
        ...prev,
        resources: [...prev.resources, newResource]
      };
    });
  };

  const handleRemoveResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  const handleStakeholderChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      stakeholderDetails: prev.stakeholderDetails.map((stakeholder, i) => 
        i === index ? { ...stakeholder, [field]: value } : stakeholder
      )
    }));
  };

  const handleAddStakeholder = () => {
    const newStakeholder = {
      name: '',
      role: '',
      organization: '',
      email: '',
      phone: '',
      influence: 'medium',
      interest: 'medium',
      availability: 'available',
      communicationPreference: 'email',
      involvement: 'regular',
      notes: ''
    };
    
    setFormData(prev => ({
      ...prev,
      stakeholderDetails: [...prev.stakeholderDetails, newStakeholder]
    }));
  };

  const handleRemoveStakeholder = (index) => {
    setFormData(prev => ({
      ...prev,
      stakeholderDetails: prev.stakeholderDetails.filter((_, i) => i !== index)
    }));
  };
  const handleAddMilestone = () => {
  setFormData(prev => ({
    ...prev,
    milestones: [
      ...prev.milestones,
      {
        name: '',
        dueDate: '',
        status: 'Not Started',
        description: ''
      }
    ]
  }));
};

const handleMilestoneChange = (index, field, value) => {
  setFormData(prev => ({
    ...prev,
    milestones: prev.milestones.map((m, i) =>
      i === index ? { ...m, [field]: value } : m
    )
  }));
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Generate the actual project code (increment counter) before saving
      // Try the same module codes we used for preview
      const moduleCodes = ['PRJ_MGMT', 'PRJ', 'PROJECT'];
      let finalProjectCode = formData.projectCode;
      
      for (const moduleCode of moduleCodes) {
        try {
          const response = await AutoIncrementService.generateNextByModuleCode(moduleCode);
          if (response && response.success && response.data && response.data.nextNumber) {
            finalProjectCode = response.data.nextNumber;
            break;
          }
        } catch (err) {
          // Try next module code
          continue;
        }
      }
      
      if (!finalProjectCode) {
        console.warn('Could not generate project code. Project may be saved without code.');
      }

      const cleanDate = (d) => (d ? new Date(d) : null);
      
      // Format data for backend with proper data types
      const projectData = {
        ...formData,
        // SAFETY 1: Ensure projectCode exists even if auto-increment failed
        projectCode: formData.projectCode || `PRJ-${Date.now()}`,
        
        // SAFETY 2: Clean dates
        startDate: cleanDate(formData.startDate),
        endDate: cleanDate(formData.endDate),
        
        // SAFETY 3: Force numbers
        budget: parseFloat(formData.budget) || 0,
        roiTarget: parseFloat(formData.roiTarget) || 0,
        budgetVarianceThreshold: parseFloat(formData.budgetVarianceThreshold) || 0,
        expenseApprovalLimit: parseFloat(formData.expenseApprovalLimit) || 0,
        // Remove projectManager - backend will set it to current user
        // Remove createdBy - backend will set it to current user
        // Convert budget breakdown to numbers
        budgetBreakdown: {
          labor: parseFloat(formData.budgetBreakdown?.labor) || 0,
          materials: parseFloat(formData.budgetBreakdown?.materials) || 0,
          equipment: parseFloat(formData.budgetBreakdown?.equipment) || 0,
          travel: parseFloat(formData.budgetBreakdown?.travel) || 0,
          other: parseFloat(formData.budgetBreakdown?.other) || 0
        },
        // Convert billing milestones with proper data types
        billingMilestones: (formData.billingMilestones || []).map(milestone => ({
          ...milestone,
          billingDate: milestone.billingDate ? new Date(milestone.billingDate) : null,
          amount: parseFloat(milestone.amount) || 0
        })),
        // Convert resources with proper data types - simplified approach
        resources: Array.isArray(formData.resources) 
          ? formData.resources.map(resource => ({
              name: String(resource.name || ''),
              type: String(resource.type || ''),
              role: String(resource.role || ''),
              rate: parseFloat(resource.rate) || 0,
              currency: String(resource.currency || 'USD'),
              hours: parseFloat(resource.hours) || 0,
              startDate: resource.startDate ? new Date(resource.startDate) : null,
              endDate: resource.endDate ? new Date(resource.endDate) : null,
              status: String(resource.status || 'active'),
              skills: Array.isArray(resource.skills) ? resource.skills.map(s => String(s)) : [],
              experience: String(resource.experience || ''),
              location: String(resource.location || ''),
              contact: String(resource.contact || ''),
              notes: String(resource.notes || '')
            }))
          : [],
        // Convert milestones with proper data types
        milestones: (formData.milestones || []).map(milestone => ({
          ...milestone,
          dueDate: milestone.dueDate ? new Date(milestone.dueDate) : null
        })),
        // Convert financial milestones with proper data types
        financialMilestones: (formData.financialMilestones || []).map(milestone => ({
          ...milestone,
          amount: parseFloat(milestone.amount) || 0,
          date: milestone.date ? new Date(milestone.date) : null
        })),
        // Convert numeric fields
        roiTarget: parseFloat(formData.roiTarget) || 0,
        budgetVarianceThreshold: parseFloat(formData.budgetVarianceThreshold) || 0,
        expenseApprovalLimit: parseFloat(formData.expenseApprovalLimit) || 0,
        // Ensure arrays are properly formatted
        objectives: Array.isArray(formData.objectives) ? formData.objectives : [],
        deliverables: Array.isArray(formData.deliverables) ? formData.deliverables : [],
        risks: Array.isArray(formData.risks) ? formData.risks : [],
        assumptions: Array.isArray(formData.assumptions) ? formData.assumptions : [],
        constraints: Array.isArray(formData.constraints) ? formData.constraints : [],
        stakeholders: Array.isArray(formData.stakeholders) ? formData.stakeholders : [],
        externalVendors: Array.isArray(formData.externalVendors) ? formData.externalVendors : [],
        stakeholderDetails: Array.isArray(formData.stakeholderDetails) ? formData.stakeholderDetails : [],
        communicationChannels: Array.isArray(formData.communicationChannels) ? formData.communicationChannels : [],
        projectTools: Array.isArray(formData.projectTools) ? formData.projectTools : [],
        integrations: Array.isArray(formData.integrations) ? formData.integrations : [],
        complianceRequirements: Array.isArray(formData.complianceRequirements) ? formData.complianceRequirements : [],
        customFields: Array.isArray(formData.customFields) ? formData.customFields : [],
        expenseCategories: Array.isArray(formData.expenseCategories) ? formData.expenseCategories : []
      };

      // Remove fields that backend will set automatically
      // Note: projectManager is kept - backend will handle it (save as name if not ObjectId)
      delete projectData.createdBy;
      
      // Safety check: Ensure resources is properly formatted
      if (typeof projectData.resources === 'string') {
        console.error('CRITICAL: Resources is a string, attempting to parse...');
        try {
          projectData.resources = JSON.parse(projectData.resources);
          console.log('Successfully parsed resources from string');
        } catch (e) {
          console.error('Failed to parse resources string:', e);
          projectData.resources = [];
        }
      }
      
      // Final validation
      if (!Array.isArray(projectData.resources)) {
        console.error('CRITICAL: Resources is not an array after processing:', typeof projectData.resources);
        projectData.resources = [];
      }

      console.log('Sending project data:', projectData);
      console.log('Project manager (should be undefined):', projectData.projectManager);
      console.log('CreatedBy (should be undefined):', projectData.createdBy);
      console.log('Resources type:', typeof projectData.resources);
      console.log('Resources length:', projectData.resources?.length);
      console.log('First resource type:', typeof projectData.resources?.[0]);
      console.log('Resources is array:', Array.isArray(projectData.resources));
      console.log('Resources:', JSON.stringify(projectData.resources, null, 2));
      console.log('Milestones:', projectData.milestones);
      console.log('Risks:', projectData.risks);
      
      // Check if resources is accidentally stringified
      if (typeof projectData.resources === 'string') {
        console.error('ERROR: Resources is a string! This should not happen.');
        console.error('String value:', projectData.resources);
      }

      // Final check before sending
      console.log('FINAL CHECK - Resources before JSON.stringify:');
      console.log('Type:', typeof projectData.resources);
      console.log('Is Array:', Array.isArray(projectData.resources));
      console.log('Value:', projectData.resources);
      
      const jsonString = JSON.stringify(projectData);
      console.log('JSON string length:', jsonString.length);
      console.log('JSON string preview:', jsonString.substring(0, 500) + '...');

      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
        method: 'POST',
        headers: API_CONFIG.HEADERS,
        body: jsonString
      });
      
      if (response.ok) {
        const responseData = await response.json();
        // Handle both old format (project object) and new format { success, data: { project } }
        const project = responseData.data?.project || responseData;
        success('Project created successfully!');
        navigate('/projects/project-management');
      } else {
        const errorData = await response.json();
        showError(errorData.message || "Backend Validation Failed");
      }
    } catch (err) {
      console.error('Error creating project:', err);
      showError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBasicInformation = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Basic Information</h3>
        <p className="section-description">Project name, description, and basic details</p>
      </div>
      
      {/* 4-Column Grid Layout */}
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Project Code *</label>
          <input
            type="text"
            className="form-control"
            value={formData.projectCode || ''}
            readOnly
            style={{
              backgroundColor: '#f9fafb',
              cursor: 'not-allowed',
              color: '#6b7280'
            }}
            placeholder="Auto-generated project code"
          />
          <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
            Project code is automatically generated
          </small>
        </div>
        
        <div className="form-group">
          <label className="form-label">Project Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter project name"
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Category</label>
          <select
            className="form-select"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            disabled={loadingCategories}
          >
            <option value="">Select category</option>
            {projectCategories.length > 0 ? (
              projectCategories.map((category) => (
                <option key={category.code} value={category.code}>
                  {category.label}
                </option>
              ))
            ) : (
              !loadingCategories && (
                <>
                  <option value="software">Software Development</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="marketing">Marketing</option>
                  <option value="research">Research & Development</option>
                  <option value="operations">Operations</option>
                  <option value="consulting">Consulting</option>
                </>
              )
            )}
          </select>
          {loadingCategories && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Loading categories...
            </small>
          )}
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Initial Status</label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="Planning">Planning</option>
            <option value="Active">Active</option>
            <option value="On Hold">On Hold</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Department</label>
          <select
            className="form-select"
            value={formData.department || ''}
            onChange={(e) => handleInputChange('department', e.target.value)}
          >
            <option value="">Select department</option>
            <option value="development">Development</option>
            <option value="engineering">Engineering</option>
            <option value="marketing">Marketing</option>
            <option value="sales">Sales</option>
            <option value="hr">Human Resources</option>
            <option value="finance">Finance</option>
            <option value="operations">Operations</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Project Type</label>
          <select
            className="form-select"
            value={formData.projectType || ''}
            onChange={(e) => handleInputChange('projectType', e.target.value)}
          >
            <option value="">Select type</option>
            <option value="internal">Internal</option>
            <option value="client">Client Project</option>
            <option value="research">Research</option>
            <option value="maintenance">Maintenance</option>
            <option value="upgrade">Upgrade</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Business Unit</label>
          <input
            type="text"
            className="form-control"
            value={formData.businessUnit || ''}
            onChange={(e) => handleInputChange('businessUnit', e.target.value)}
            placeholder="e.g., North America, EMEA"
          />
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Project Description</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Describe the project objectives, scope, and key deliverables..."
        />
      </div>
    </div>
  );

  const renderTimelineBudget = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Timeline & Budget</h3>
        <p className="section-description">Schedule, duration, and financial planning</p>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Start Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">End Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
          />
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Estimated Duration</label>
          <input
            type="text"
            className="form-control"
            value={formData.estimatedDuration}
            onChange={(e) => handleInputChange('estimatedDuration', e.target.value)}
            placeholder="e.g., 3 months, 6 weeks"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Budget</label>
          <div className="budget-input-group">
            <input
              type="number"
              className="form-control budget-amount"
              value={formData.budget}
              onChange={(e) => handleInputChange('budget', e.target.value)}
              placeholder="0"
            />
            <select
              className="form-select budget-currency"
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Cost Center</label>
        <input
          type="text"
          className="form-control"
          value={formData.costCenter}
          onChange={(e) => handleInputChange('costCenter', e.target.value)}
          placeholder="Enter cost center code"
        />
      </div>
    </div>
  );

  const renderTeamResources = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Team & Resources</h3>
        <p className="section-description">Team members, stakeholders, and resources</p>
      </div>
      <div className="form-group">
        <label className="form-label">Project Manager *</label>
        <select
  className="form-select"
  value={formData.projectManager}
  onChange={(e) => handleInputChange('projectManager', e.target.value)}
  required
>
  <option value="">Select Project Manager</option>

  {projectManagers.map(member => (
    <option key={member._id} value={member._id}>
      {member.name} — {member.role}
    </option>
  ))}
</select>
      </div>
      
      
      <div className="section-header">
        <h3 className="section-title">Stakeholder Management</h3>
        <p className="section-description">Detailed stakeholder information with influence, interest, and communication preferences</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Project Stakeholders</label>
        <div className="stakeholders-container">
          {formData.stakeholderDetails.map((stakeholder, index) => (
            <div key={index} className="stakeholder-item">
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Stakeholder Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={stakeholder.name}
                    onChange={(e) => handleStakeholderChange(index, 'name', e.target.value)}
                    placeholder="e.g., John Smith"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <input
                    type="text"
                    className="form-control"
                    value={stakeholder.role}
                    onChange={(e) => handleStakeholderChange(index, 'role', e.target.value)}
                    placeholder="e.g., Client Sponsor"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Organization</label>
                  <input
                    type="text"
                    className="form-control"
                    value={stakeholder.organization}
                    onChange={(e) => handleStakeholderChange(index, 'organization', e.target.value)}
                    placeholder="e.g., ABC Corp"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Influence Level</label>
                  <select
                    className="form-select"
                    value={stakeholder.influence}
                    onChange={(e) => handleStakeholderChange(index, 'influence', e.target.value)}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>
              
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={stakeholder.email}
                    onChange={(e) => handleStakeholderChange(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={stakeholder.phone}
                    onChange={(e) => handleStakeholderChange(index, 'phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Interest Level</label>
                  <select
                    className="form-select"
                    value={stakeholder.interest}
                    onChange={(e) => handleStakeholderChange(index, 'interest', e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Availability</label>
                  <select
                    className="form-select"
                    value={stakeholder.availability}
                    onChange={(e) => handleStakeholderChange(index, 'availability', e.target.value)}
                  >
                    <option value="available">Available</option>
                    <option value="limited">Limited</option>
                    <option value="unavailable">Unavailable</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Communication Preference</label>
                  <select
                    className="form-select"
                    value={stakeholder.communicationPreference}
                    onChange={(e) => handleStakeholderChange(index, 'communicationPreference', e.target.value)}
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="meeting">In-Person Meeting</option>
                    <option value="video">Video Call</option>
                    <option value="any">Any Method</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Involvement Level</label>
                  <select
                    className="form-select"
                    value={stakeholder.involvement}
                    onChange={(e) => handleStakeholderChange(index, 'involvement', e.target.value)}
                  >
                    <option value="minimal">Minimal</option>
                    <option value="regular">Regular</option>
                    <option value="frequent">Frequent</option>
                    <option value="constant">Constant</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input
                  type="text"
                  className="form-control"
                  value={stakeholder.notes}
                  onChange={(e) => handleStakeholderChange(index, 'notes', e.target.value)}
                  placeholder="Additional stakeholder information"
                />
              </div>
              
              <div className="stakeholder-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveStakeholder(index)}
                >
                  <X size={16} />
                  Remove Stakeholder
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-stakeholder">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddStakeholder}
            >
              <Plus size={16} />
              Add Stakeholder
            </button>
          </div>
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Resource Management</h3>
        <p className="section-description">Detailed resource information with rates, skills, and availability</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Project Resources</label>
        <div className="resources-container">
          {formData.resources.map((resource, index) => (
            <div key={index} className="resource-item">
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Resource Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.name}
                    onChange={(e) => handleResourceChange(index, 'name', e.target.value)}
                    placeholder="e.g., John Smith"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Resource Type</label>
                  <select
                    className="form-select"
                    value={resource.type}
                    onChange={(e) => handleResourceChange(index, 'type', e.target.value)}
                  >
                    <option value="">Select type</option>
                    <option value="employee">Employee</option>
                    <option value="contractor">Contractor</option>
                    <option value="consultant">Consultant</option>
                    <option value="vendor">Vendor</option>
                    <option value="freelancer">Freelancer</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.role}
                    onChange={(e) => handleResourceChange(index, 'role', e.target.value)}
                    placeholder="e.g., Senior Developer"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Rate</label>
                  <div className="budget-input-group">
                    <input
                      type="number"
                      className="form-control budget-amount"
                      value={resource.rate}
                      onChange={(e) => handleResourceChange(index, 'rate', e.target.value)}
                      placeholder="0"
                    />
                    <select 
                      className="form-select budget-currency"
                      value={resource.currency}
                      onChange={(e) => handleResourceChange(index, 'currency', e.target.value)}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Hours/Week</label>
                  <input
                    type="number"
                    className="form-control"
                    value={resource.hours}
                    onChange={(e) => handleResourceChange(index, 'hours', e.target.value)}
                    placeholder="40"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={resource.startDate}
                    onChange={(e) => handleResourceChange(index, 'startDate', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={resource.endDate}
                    onChange={(e) => handleResourceChange(index, 'endDate', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={resource.status}
                    onChange={(e) => handleResourceChange(index, 'status', e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Experience Level</label>
                  <select
                    className="form-select"
                    value={resource.experience}
                    onChange={(e) => handleResourceChange(index, 'experience', e.target.value)}
                  >
                    <option value="">Select experience</option>
                    <option value="junior">Junior (0-2 years)</option>
                    <option value="mid">Mid-level (3-5 years)</option>
                    <option value="senior">Senior (6-10 years)</option>
                    <option value="expert">Expert (10+ years)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.location}
                    onChange={(e) => handleResourceChange(index, 'location', e.target.value)}
                    placeholder="e.g., New York, Remote"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contact</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.contact}
                    onChange={(e) => handleResourceChange(index, 'contact', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    value={resource.notes}
                    onChange={(e) => handleResourceChange(index, 'notes', e.target.value)}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
              
              <div className="resource-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveResource(index)}
                >
                  <X size={16} />
                  Remove Resource
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-resource">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddResource}
            >
              <Plus size={16} />
              Add Resource
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProjectDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Project Details</h3>
        <p className="section-description">Objectives, deliverables, and milestones</p>
      </div>
      <div className="form-group">
        <label className="form-label">Project Objectives</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add objective"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('objectives', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  handleArrayAdd('objectives', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.objectives.map((objective, index) => (
              <div key={index} className="array-item">
                <span>{objective}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('objectives', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Key Deliverables</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add deliverable"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('deliverables', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  handleArrayAdd('deliverables', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.deliverables.map((deliverable, index) => (
              <div key={index} className="array-item">
                <span>{deliverable}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('deliverables', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="form-group">
  <label className="form-label">Milestones</label>

  {formData.milestones.map((milestone, index) => (
    <div key={index} className="form-row" style={{ marginBottom: '12px' }}>
      <div className="form-group" style={{ flex: 2 }}>
        <input
          type="text"
          className="form-control"
          placeholder="Milestone name"
          value={milestone.name}
          onChange={(e) =>
            handleMilestoneChange(index, 'name', e.target.value)
          }
          required
        />
      </div>

      <div className="form-group" style={{ flex: 1 }}>
        <input
          type="date"
          className="form-control"
          value={milestone.dueDate}
          onChange={(e) =>
            handleMilestoneChange(index, 'dueDate', e.target.value)
          }
          required
        />
      </div>

      <button
        type="button"
        className="btn btn-outline-danger"
        onClick={() =>
          setFormData(prev => ({
            ...prev,
            milestones: prev.milestones.filter((_, i) => i !== index)
          }))
        }
      >
        ✕
      </button>
    </div>
  ))}

  <button
    type="button"
    className="btn btn-outline"
    onClick={handleAddMilestone}
  >
    + Add Milestone
  </button>
</div>
          </div>

  );

  const renderCommunication = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Communication</h3>
        <p className="section-description">Reporting, meetings, and collaboration</p>
      </div>
      <div className="form-group">
        <label className="form-label">Reporting Frequency</label>
        <select
          className="form-select"
          value={formData.reportingFrequency}
          onChange={(e) => handleInputChange('reportingFrequency', e.target.value)}
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="bi-weekly">Bi-weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>
      
      <div className="form-group">
        <label className="form-label">Communication Channels</label>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="email"
              checked={formData.communicationChannels.includes('email')}
              onChange={(e) => {
                const channels = formData.communicationChannels;
                if (e.target.checked) {
                  handleInputChange('communicationChannels', [...channels, 'email']);
                } else {
                  handleInputChange('communicationChannels', channels.filter(c => c !== 'email'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="email">Email</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="slack"
              checked={formData.communicationChannels.includes('slack')}
              onChange={(e) => {
                const channels = formData.communicationChannels;
                if (e.target.checked) {
                  handleInputChange('communicationChannels', [...channels, 'slack']);
                } else {
                  handleInputChange('communicationChannels', channels.filter(c => c !== 'slack'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="slack">Slack</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="teams"
              checked={formData.communicationChannels.includes('teams')}
              onChange={(e) => {
                const channels = formData.communicationChannels;
                if (e.target.checked) {
                  handleInputChange('communicationChannels', [...channels, 'teams']);
                } else {
                  handleInputChange('communicationChannels', channels.filter(c => c !== 'teams'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="teams">Microsoft Teams</label>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Meeting Schedule</label>
        <input
          type="text"
          className="form-control"
          value={formData.meetingSchedule}
          onChange={(e) => handleInputChange('meetingSchedule', e.target.value)}
          placeholder="e.g., Every Monday at 10:00 AM"
        />
      </div>
    </div>
  );

  const renderIntegration = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Integration & Tools</h3>
        <p className="section-description">Project tools and system integrations</p>
      </div>
      <div className="form-group">
        <label className="form-label">Project Tools</label>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="jira"
              checked={formData.projectTools.includes('jira')}
              onChange={(e) => {
                const tools = formData.projectTools;
                if (e.target.checked) {
                  handleInputChange('projectTools', [...tools, 'jira']);
                } else {
                  handleInputChange('projectTools', tools.filter(t => t !== 'jira'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="jira">Jira</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="asana"
              checked={formData.projectTools.includes('asana')}
              onChange={(e) => {
                const tools = formData.projectTools;
                if (e.target.checked) {
                  handleInputChange('projectTools', [...tools, 'asana']);
                } else {
                  handleInputChange('projectTools', tools.filter(t => t !== 'asana'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="asana">Asana</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="trello"
              checked={formData.projectTools.includes('trello')}
              onChange={(e) => {
                const tools = formData.projectTools;
                if (e.target.checked) {
                  handleInputChange('projectTools', [...tools, 'trello']);
                } else {
                  handleInputChange('projectTools', tools.filter(t => t !== 'trello'));
                }
              }}
            />
            <label className="form-check-label" htmlFor="trello">Trello</label>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Document Repository</label>
        <input
          type="text"
          className="form-control"
          value={formData.documentRepository}
          onChange={(e) => handleInputChange('documentRepository', e.target.value)}
          placeholder="e.g., SharePoint, Google Drive, Confluence"
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Knowledge Base</label>
        <input
          type="text"
          className="form-control"
          value={formData.knowledgeBase}
          onChange={(e) => handleInputChange('knowledgeBase', e.target.value)}
          placeholder="e.g., Confluence, Notion, Wiki"
        />
      </div>
    </div>
  );

  const renderCompliance = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Compliance & Security</h3>
        <p className="section-description">Security, compliance, and approval workflows</p>
      </div>
      <div className="form-group">
        <label className="form-label">Security Level</label>
        <select
          className="form-select"
          value={formData.securityLevel}
          onChange={(e) => handleInputChange('securityLevel', e.target.value)}
        >
          <option value="standard">Standard</option>
          <option value="high">High</option>
          <option value="confidential">Confidential</option>
          <option value="restricted">Restricted</option>
        </select>
      </div>
      
      <div className="form-group">
        <label className="form-label">Data Classification</label>
        <select
          className="form-select"
          value={formData.dataClassification}
          onChange={(e) => handleInputChange('dataClassification', e.target.value)}
        >
          <option value="public">Public</option>
          <option value="internal">Internal</option>
          <option value="confidential">Confidential</option>
          <option value="restricted">Restricted</option>
        </select>
      </div>
      
      <div className="form-group">
        <label className="form-label">Approval Workflow</label>
        <select
          className="form-select"
          value={formData.approvalWorkflow}
          onChange={(e) => handleInputChange('approvalWorkflow', e.target.value)}
        >
          <option value="">Select workflow</option>
          <option value="standard">Standard Approval</option>
          <option value="expedited">Expedited Approval</option>
          <option value="executive">Executive Approval</option>
          <option value="board">Board Approval</option>
        </select>
      </div>
    </div>
  );

  const renderFinanceBudgeting = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Budget Breakdown</h3>
        <p className="section-description">Detailed budget allocation by category</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Labor Costs</label>
          <div className="budget-input-group">
            <input
              type="number"
              className="form-control budget-amount"
              value={formData.budgetBreakdown.labor}
              onChange={(e) => handleInputChange('budgetBreakdown', {...formData.budgetBreakdown, labor: e.target.value})}
              placeholder="0"
            />
            <select className="form-select budget-currency">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Materials</label>
          <div className="budget-input-group">
            <input
              type="number"
              className="form-control budget-amount"
              value={formData.budgetBreakdown.materials}
              onChange={(e) => handleInputChange('budgetBreakdown', {...formData.budgetBreakdown, materials: e.target.value})}
              placeholder="0"
            />
            <select className="form-select budget-currency">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Equipment</label>
          <div className="budget-input-group">
            <input
              type="number"
              className="form-control budget-amount"
              value={formData.budgetBreakdown.equipment}
              onChange={(e) => handleInputChange('budgetBreakdown', {...formData.budgetBreakdown, equipment: e.target.value})}
              placeholder="0"
            />
            <select className="form-select budget-currency">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Travel & Expenses</label>
          <div className="budget-input-group">
            <input
              type="number"
              className="form-control budget-amount"
              value={formData.budgetBreakdown.travel}
              onChange={(e) => handleInputChange('budgetBreakdown', {...formData.budgetBreakdown, travel: e.target.value})}
              placeholder="0"
            />
            <select className="form-select budget-currency">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Financial Controls</h3>
        <p className="section-description">Budget approval and financial reporting settings</p>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Budget Approval Required</label>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="budgetApprovalRequired"
              checked={formData.budgetApprovalRequired}
              onChange={(e) => handleInputChange('budgetApprovalRequired', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="budgetApprovalRequired">Require budget approval</label>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Budget Approver</label>
          <select
            className="form-select"
            value={formData.budgetApprover}
            onChange={(e) => handleInputChange('budgetApprover', e.target.value)}
          >
            <option value="">Select approver</option>
            <option value="finance-manager">Finance Manager</option>
            <option value="director">Director</option>
            <option value="ceo">CEO</option>
          </select>
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">ROI Target (%)</label>
          <input
            type="number"
            className="form-control"
            value={formData.roiTarget}
            onChange={(e) => handleInputChange('roiTarget', e.target.value)}
            placeholder="e.g., 15"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Budget Variance Threshold (%)</label>
          <input
            type="number"
            className="form-control"
            value={formData.budgetVarianceThreshold}
            onChange={(e) => handleInputChange('budgetVarianceThreshold', e.target.value)}
            placeholder="e.g., 10"
          />
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Financial Reporting Frequency</label>
        <select
          className="form-select"
          value={formData.financialReporting}
          onChange={(e) => handleInputChange('financialReporting', e.target.value)}
        >
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="as-needed">As Needed</option>
        </select>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Billing Milestones</h3>
        <p className="section-description">Set up invoice triggers, amounts, and billing dates</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Billing Milestones</label>
        <div className="billing-milestones-container">
          {formData.billingMilestones.map((milestone, index) => (
            <div key={index} className="billing-milestone-item">
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Milestone Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={milestone.name}
                    onChange={(e) => handleBillingMilestoneChange(index, 'name', e.target.value)}
                    placeholder="e.g., Project Kickoff"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Billing Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={milestone.billingDate}
                    onChange={(e) => handleBillingMilestoneChange(index, 'billingDate', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Amount</label>
                  <div className="budget-input-group">
                    <input
                      type="number"
                      className="form-control budget-amount"
                      value={milestone.amount}
                      onChange={(e) => handleBillingMilestoneChange(index, 'amount', e.target.value)}
                      placeholder="0"
                    />
                    <select 
                      className="form-select budget-currency"
                      value={milestone.currency}
                      onChange={(e) => handleBillingMilestoneChange(index, 'currency', e.target.value)}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Trigger Condition</label>
                  <select
                    className="form-select"
                    value={milestone.trigger}
                    onChange={(e) => handleBillingMilestoneChange(index, 'trigger', e.target.value)}
                  >
                    <option value="date">On Date</option>
                    <option value="milestone">On Milestone Completion</option>
                    <option value="percentage">On Project % Complete</option>
                    <option value="deliverable">On Deliverable</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={milestone.description}
                    onChange={(e) => handleBillingMilestoneChange(index, 'description', e.target.value)}
                    placeholder="Billing milestone description"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Auto-Generate Invoice</label>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`autoInvoice-${index}`}
                      checked={milestone.autoGenerate}
                      onChange={(e) => handleBillingMilestoneChange(index, 'autoGenerate', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`autoInvoice-${index}`}>
                      Auto-generate invoice
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="billing-milestone-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveBillingMilestone(index)}
                >
                  <X size={16} />
                  Remove Milestone
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-billing-milestone">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddBillingMilestone}
            >
              <Plus size={16} />
              Add Billing Milestone
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Advanced Settings</h3>
        <p className="section-description">Notifications, tracking, and custom fields</p>
      </div>
      <div className="form-group">
        <label className="form-label">Notifications & Alerts</label>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="autoNotifications"
              checked={formData.autoNotifications}
              onChange={(e) => handleInputChange('autoNotifications', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="autoNotifications">Auto Notifications</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="timeTracking"
              checked={formData.timeTracking}
              onChange={(e) => handleInputChange('timeTracking', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="timeTracking">Time Tracking</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="budgetAlerts"
              checked={formData.budgetAlerts}
              onChange={(e) => handleInputChange('budgetAlerts', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="budgetAlerts">Budget Alerts</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="riskMonitoring"
              checked={formData.riskMonitoring}
              onChange={(e) => handleInputChange('riskMonitoring', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="riskMonitoring">Risk Monitoring</label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            {renderBasicInformation()}
            {renderTimelineBudget()}
          </div>
        );
      case 1:
        return (
          <div>
            {renderTeamResources()}
            {renderProjectDetails()}
          </div>
        );
      case 2:
        return renderFinanceBudgeting();
      case 3:
        return (
          <div>
            {renderCommunication()}
            {renderIntegration()}
          </div>
        );
      case 4:
        return (
          <div>
            {renderCompliance()}
            {renderAdvancedSettings()}
          </div>
        );
      default:
        return (
          <div>
            {renderBasicInformation()}
            {renderTimelineBudget()}
          </div>
        );
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Create New Project"
        description="Set up a comprehensive project with all necessary details and configurations"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
          <form onSubmit={currentStep === STEPS.length - 1 ? handleSubmit : handleNext} style={{ width: '100%' }}>
            <div style={{ width: '100%', marginBottom: '24px' }}>
              <FormStepper
                steps={STEPS}
                currentStep={currentStep}
                onStepClick={handleStepClick}
              />
            </div>

            <div className="project-form-card">
              {renderStepContent()}
            </div>

            <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
              <div>
                {testMode && (
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={fillTestData}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: '#ffffff',
                      borderColor: '#d1d5db'
                    }}
                  >
                    <Database size={18} />
                    Fill Test Data
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={currentStep === 0}
                    onClick={handleBack}
                    style={{ minWidth: '100px', borderRadius: '0', padding: '12px 24px' }}
                  >
                    Back
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/projects/project-management')}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                    style={{ minWidth: '140px' }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Saving...
                      </>
                    ) : currentStep < STEPS.length - 1 ? (
                      'Next'
                    ) : (
                      <>
                        <Save size={18} />
                        Create Project
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateProject;